import L from 'leaflet';
import { agregarCapaAnimada, Z_SATELITE } from './capaAnimada';

/**
 * Satélite infrarrojo — las tormentas, en todo el país.
 *
 * Existe por lo que el radar NO puede: de las zonas agrícolas de México sólo
 * Sabinas queda dentro del alcance de un radar. Montemorelos, Cd. Victoria,
 * Hermosillo, el Bajío y Veracruz están fuera, y para todas ésas el radar es
 * una capa vacía. Ésta las cubre todas.
 *
 * El dato es GOES-East de la NOAA, servido por NASA GIBS, dominio público y
 * sin llave. Se midió su atraso real contra el reloj: unos 23 minutos, con
 * cuadro nuevo cada 10. Cubre el continente completo.
 *
 * QUÉ ESTÁ VIENDO, para no confundirlo con el radar: esto no mide lluvia,
 * mide la temperatura de lo más alto que ve desde arriba. Una nube de
 * tormenta es alta y por lo tanto muy fría, así que resalta contra el suelo
 * tibio. Sirve para "hay una celda y va hacia allá", no para "están cayendo
 * 12 mm". Y la resolución del satélite son ~2 km contra los 67 m del radar:
 * por eso el tope de zoom es bajo, no porque falte servidor.
 */

/**
 * Minutos hacia atrás de cada cuadro.
 *
 * Arranca en 30 y no en 0 porque el dato tarda: se midió un atraso de ~23
 * minutos, y pedir un cuadro que todavía no existe devuelve un mosaico
 * vacío. Treinta deja margen para un día en que el satélite venga lento.
 */
const CUADROS = [80, 70, 60, 50, 40, 30];

/**
 * Transparencia de las nubes.
 *
 * Alta a propósito, porque el pintado ya recorta el fondo (ver CLASE). Lo
 * que queda es el blanco de las nubes altas sobre el mapa.
 */
const OPACIDAD = 0.85;

/**
 * Tope de zoom, impuesto por la física y no por el servidor.
 *
 * GIBS publica esta capa hasta el nivel 6 porque el sensor tiene ~2 km de
 * resolución; a partir de ahí no habría nada nuevo que mostrar.
 */
const ZOOM_NATIVO_MAX = 6;

/**
 * Clase con el truco que hace usable esta capa.
 *
 * El infrarrojo llega 100% opaco —se comprobó, cero pixeles transparentes—
 * así que puesto encima taparía el mapa por completo, sensores incluidos.
 * Pero en infrarrojo lo frío se pinta claro y lo tibio oscuro: las nubes de
 * tormenta son casi blancas y el suelo casi negro.
 *
 * Con el modo de fusión `screen`, el negro deja pasar el fondo intacto y el
 * blanco se impone. O sea que el suelo desaparece solo y quedan únicamente
 * las nubes, sin procesar un pixel a mano. El estilo va en index.css.
 */
const CLASE = 'capa-satelite-ir';

const AHORA_MS = () => Date.now();

/**
 * Arma la marca de tiempo que pide GIBS.
 *
 * El servicio no acepta cualquier instante: los cuadros existen cada diez
 * minutos en punto, así que hay que redondear hacia abajo o la petición se
 * va por un mosaico que no existe.
 */
function marcaDeTiempo(minutosAtras: number): string {
  const d = new Date(AHORA_MS() - minutosAtras * 60000);
  d.setUTCSeconds(0, 0);
  d.setUTCMinutes(Math.floor(d.getUTCMinutes() / 10) * 10);
  return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

const plantilla = (minutos: number) =>
  'https://gibs.earthdata.nasa.gov/wmts/epsg3857/best/GOES-East_ABI_Band13_Clean_Infrared' +
  // GIBS ordena la matriz como fila/columna, que es y/x — al revés de lo
  // acostumbrado. Invertirlo trae mosaicos de otra parte del mundo.
  `/default/${marcaDeTiempo(minutos)}/GoogleMapsCompatible_Level6/{z}/{y}/{x}.png`;

export function agregarCapaSatelite(
  map: L.Map,
  etiqueta: string,
  textoHora: (hora: string) => string,
): () => void {
  return agregarCapaAnimada(map, {
    cuadros: CUADROS,
    urlDe: plantilla,
    etiqueta,
    textoHora,
    opacidad: OPACIDAD,
    maxNativeZoom: ZOOM_NATIVO_MAX,
    zIndex: Z_SATELITE,
    atribucion: 'Satélite: NOAA GOES-East / NASA GIBS',
    preferencia: 'mapa_satelite',
    clase: CLASE,
    // Apagada por omisión, al revés que el radar. Ésta sí pinta algo casi
    // siempre —nubes hay todos los días— y encima llega borrosa por los 2 km
    // del sensor. Prendida sin pedirla, ensucia el mapa más de lo que ayuda:
    // que la encienda quien esté buscando una tormenta.
    porOmision: false,
  });
}
